import { MonitorFilterOption, MonitorMeta, MonitorQueryState, SortOrder } from "./types";

const FILTER_PARAM_MAP: Record<string, string> = {
  needs_review: "filter[status]",
  active_tasks: "filter[tasks]",
  high: "filter[high]",
  moderate: "filter[moderate]",
  low: "filter[low]",
  missed: "filter[missed]",
  technical: "filter[technical]",
  triage: "filter[triage]",
  track_and_trend: "filter[track_and_trend]"
};

// HHMCOVID-5726 — Patient Status is multi-select (3 simultaneously-selectable
// values), so it can't use the 1-value-per-param scheme above. It's serialized
// as a single comma-joined param instead, matching the existing comma-list
// convention already used elsewhere in this codebase (monitor_xml's `status`
// param).
const PATIENT_STATUS_PARAM = "filter[patient_status]";

function getSessionDataFn() {
  return (window as any).getSessionData;
}

function setSessionDataFn() {
  return (window as any).setSessionData;
}

export function getFilterParamMap() {
  return FILTER_PARAM_MAP;
}

export function buildFilterOptions(meta: MonitorMeta): MonitorFilterOption[] {
  const labels = meta.filter_labels || ({} as any);
  const options: MonitorFilterOption[] = [
    { value: "needs_review", label: labels.need_review || "Need Review" },
    { value: "active_tasks", label: labels.active_tasks || "Active Tasks" },
    { value: "high", label: labels.high_severity || "Significant" },
    { value: "moderate", label: labels.moderate_severity || "Marginal" },
    { value: "low", label: labels.low_severity || "Low" },
    { value: "missed", label: labels.missed_severity || "Missed" },
    { value: "technical", label: labels.technical_severity || "Technical" }
  ];

  if (meta.track_and_trend_filter) {
    options.push({ value: "track_and_trend", label: labels.track_and_trend || "Track and Trend" });
    options.push({ value: "triage", label: labels.triage || "Triage" });
  }

  return options;
}

// HHMCOVID-5726 — built dynamically from meta.patient_status_labels rather than
// a hardcoded value list, per the ticket's "do not hardcode filtering values"
// note (a 4th Patient Status value added later needs no frontend change here).
export function buildPatientStatusOptions(meta: MonitorMeta): MonitorFilterOption[] {
  const labels: Record<string, string> = meta.patient_status_labels || {};
  // HHMCOVID-5726 (PR #398 review) — filter to available_patient_statuses when
  // present (e.g. excludes "Consent Missing" for tenants that don't track
  // consent, since it could never match a patient). Falls back to every label
  // key if the meta field is absent, matching the original behaviour.
  const available = meta.available_patient_statuses || Object.keys(labels);
  return available.filter((value) => labels[value] != null).map((value) => ({ value, label: labels[value] }));
}

function defaultSearchField(meta: MonitorMeta): string {
  const explicitDefault = meta.search_items.find((item) => item.default);
  if (explicitDefault) {
    return explicitDefault.value;
  }

  if (meta.search_items.length > 0) {
    return meta.search_items[0].value;
  }

  return "patient*name";
}

// [HHMCOVID-6272] Defaults come entirely from the server (see `monitor_default_filters` in
// encounter_controller.rb) — no hardcoded fallback pre-selection here.
function defaultFilters(meta: MonitorMeta): string[] {
  return meta.default_filters || [];
}

export function defaultQueryState(defaultSort: string, defaultSortOrder: SortOrder, meta: MonitorMeta): MonitorQueryState {
  return {
    page: 1,
    rp: 50,
    sortname: defaultSort,
    sortorder: defaultSortOrder,
    query: "",
    qtype: defaultSearchField(meta),
    qdesc: "",
    program_name: meta.default_program_name || "",
    treatment_team: meta.default_treatment_team || "",
    care_manager: meta.default_care_manager || "",
    filters: defaultFilters(meta),
    patientStatuses: []
  };
}

function toInt(value: any, fallback: number): number {
  const parsed = parseInt(value, 10);
  return parsed > 0 ? parsed : fallback;
}

const passthroughByKey = new Map<string, Record<string, any>>();

const KNOWN_STATE_KEYS = new Set<string>([
  "page",
  "rp",
  "sortname",
  "sortorder",
  "query",
  "qtype",
  "qdesc",
  "filter[program_name]",
  "filter[treatment_team]",
  "filter[care_manager]",
  PATIENT_STATUS_PARAM
]);

// PR #398 review — `availablePatientStatuses` sanitizes a persisted selection
// against what's actually offered right now (see `available_patient_statuses`
// in types.ts). Without this, a value saved before a tenant config change
// (e.g. "consent_missing" before consent tracking was disabled) would keep
// silently filtering every subsequent fetch — indefinitely, and invisibly,
// since FilterBar's own displayed state is sanitized separately and would show
// 0 active filters while the grid was still actually narrowed.
export function loadQueryState(stateKey: string, fallback: MonitorQueryState, availablePatientStatuses: string[]): MonitorQueryState {
  const getter = getSessionDataFn();
  if (!getter) {
    return fallback;
  }

  const rawState = getter(stateKey);
  if (!rawState) {
    return fallback;
  }

  const filters = Object.keys(FILTER_PARAM_MAP).filter((filterName) => {
    const paramKey = FILTER_PARAM_MAP[filterName];
    return !!rawState[paramKey];
  });

  // Preserve any unknown keys (bottomHeight, col_width_info, etc.) so they
  // survive a round-trip into React and back to legacy.
  const passthrough: Record<string, any> = {};
  Object.keys(rawState).forEach((key) => {
    if (KNOWN_STATE_KEYS.has(key) || FILTER_PARAM_MAP[key.replace(/^filter\[|\]$/g, "")] === key) {
      return;
    }
    if (Object.values(FILTER_PARAM_MAP).includes(key)) {
      return;
    }
    passthrough[key] = rawState[key];
  });
  passthroughByKey.set(stateKey, passthrough);

  const patientStatusRaw = rawState[PATIENT_STATUS_PARAM];
  const allowedPatientStatuses = new Set(availablePatientStatuses);
  const patientStatuses = patientStatusRaw
    ? Array.from(new Set(String(patientStatusRaw).split(",").filter((value) => allowedPatientStatuses.has(value))))
    : fallback.patientStatuses;

  return {
    page: toInt(rawState.page, fallback.page),
    rp: toInt(rawState.rp, fallback.rp),
    sortname: rawState.sortname || fallback.sortname,
    sortorder: rawState.sortorder === "asc" ? "asc" : "desc",
    query: rawState.query || fallback.query,
    qtype: rawState.qtype || fallback.qtype,
    qdesc: rawState.qdesc || fallback.qdesc,
    program_name: rawState["filter[program_name]"] || fallback.program_name,
    treatment_team: rawState["filter[treatment_team]"] || fallback.treatment_team,
    care_manager: rawState["filter[care_manager]"] || fallback.care_manager,
    filters: filters.length > 0 ? filters : fallback.filters,
    patientStatuses
  };
}

export function saveQueryState(stateKey: string, state: MonitorQueryState) {
  const setter = setSessionDataFn();
  if (!setter) {
    return;
  }

  const passthrough = passthroughByKey.get(stateKey) || {};
  const payload: Record<string, any> = {
    ...passthrough,
    page: state.page,
    rp: state.rp,
    sortname: state.sortname,
    sortorder: state.sortorder,
    qtype: state.qtype,
    qdesc: state.qdesc,
    query: state.query,
    "filter[program_name]": state.program_name,
    "filter[treatment_team]": state.treatment_team,
    "filter[care_manager]": state.care_manager
  };

  Object.keys(FILTER_PARAM_MAP).forEach((filterName) => {
    const key = FILTER_PARAM_MAP[filterName];
    payload[key] = state.filters.includes(filterName) ? filterName : "";
  });
  payload[PATIENT_STATUS_PARAM] = state.patientStatuses.join(",");

  setter(stateKey, payload);
}

// HHMCOVID-6327 — AC2/AC3 each persist a value under a key kept separate
// from `stateKey`'s query-state bucket, since `saveQueryState` re-writes
// from a passthrough snapshot captured once at load — a value written
// independently (e.g. on every resize) would get clobbered on the next
// sort/filter/page change. Factors the shared key/read/write/validate shape so each field is just a suffix + a parse function.
function makeSessionField<T>(suffix: string, parse: (raw: any) => T | null) {
  const key = (stateKey: string) => `${stateKey}.${suffix}`;
  return {
    load(stateKey: string): T | null {
      const getter = getSessionDataFn();
      return getter ? parse(getter(key(stateKey))) : null;
    },
    save(stateKey: string, value: T) {
      const setter = setSessionDataFn();
      if (setter) {
        setter(key(stateKey), value);
      }
    }
  };
}

const gridHeightField = makeSessionField<number>("height", (raw) => {
  const height = typeof raw === "number" ? raw : parseInt(raw, 10);
  return Number.isFinite(height) && height > 0 ? height : null;
});
export const loadGridHeight = gridHeightField.load;
export const saveGridHeight = gridHeightField.save;

// HHMCOVID-6327 AC3 — restore the previously-focused patient row on return
// to the dashboard.
export interface PersistedSelection {
  selectedIds: number[];
  focusedPatientId: number | null;
}

const selectionField = makeSessionField<PersistedSelection>("selection", (raw) => {
  if (!raw || !Array.isArray(raw.selectedIds)) {
    return null;
  }
  const selectedIds = raw.selectedIds.filter((id: unknown) => typeof id === "number");
  const focusedPatientId = typeof raw.focusedPatientId === "number" ? raw.focusedPatientId : null;
  return { selectedIds, focusedPatientId };
});
export const loadSelection = selectionField.load;
export const saveSelection = selectionField.save;

// HHMCOVID-6327 AC2 — persist the auto-computed fill height (see
// useFillAvailableHeight) so it survives in-app navigation the same way
// manual resize persistence does (same AC, same mechanism, different field).
// Note this is only ever a first-paint placeholder: useFillAvailableHeight
// always re-measures live on every mount rather than trusting this value
// outright (see that hook's own comments for why).
export interface PersistedFillHeight {
  fillHeight: number;
}

const fillHeightField = makeSessionField<PersistedFillHeight>("fillHeight", (raw) => {
  if (!raw || typeof raw.fillHeight !== "number" || raw.fillHeight <= 0) {
    return null;
  }
  return { fillHeight: raw.fillHeight };
});
export const loadFillHeight = fillHeightField.load;
export const saveFillHeight = fillHeightField.save;

// HHMCOVID-6343 — per-column width overrides from a header-border drag, keyed by
// GridColumn.key. Not by sortname/abbr as legacy's col_width_info was
// (flexigrid.js:465-470), which is why that format is unusable here: icon-only
// columns (lock, key_info) have no sortname and would all collapse onto "".
//
// Own session field rather than saveQueryState's bucket, for the same reason as
// the two fields above. Validates shape only — useColumnWidths owns the MIN/MAX
// clamp, so state.ts needn't import from a hook.
const columnWidthsField = makeSessionField<Record<string, number>>("columnWidths", (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const widths: Record<string, number> = {};
  Object.keys(raw).forEach((key) => {
    const value = raw[key];
    const width = typeof value === "number" ? value : parseInt(value, 10);
    if (Number.isFinite(width) && width > 0) {
      widths[key] = Math.round(width);
    }
  });
  return Object.keys(widths).length > 0 ? widths : null;
});
export const loadColumnWidths = columnWidthsField.load;
export const saveColumnWidths = columnWidthsField.save;

export function buildDashboardParams(state: MonitorQueryState): URLSearchParams {
  const params = new URLSearchParams();
  params.set("page", String(state.page));
  params.set("rp", String(state.rp));
  params.set("sortname", state.sortname);
  params.set("sortorder", state.sortorder);
  params.set("qtype", state.qtype);
  params.set("qdesc", state.qdesc || "");
  params.set("query", state.query);
  params.set("filter[program_name]", state.program_name || "");
  params.set("filter[treatment_team]", state.treatment_team || "");
  params.set("filter[care_manager]", state.care_manager || "");

  Object.keys(FILTER_PARAM_MAP).forEach((filterName) => {
    const paramKey = FILTER_PARAM_MAP[filterName];
    params.set(paramKey, state.filters.includes(filterName) ? filterName : "");
  });
  params.set(PATIENT_STATUS_PARAM, state.patientStatuses.join(","));

  return params;
}
